import { action, computed, observable, runInAction, makeObservable } from 'mobx'
import RootStore from '@App/stores/root-store'
import { RecordingGroupList } from '@Entities/matching/recording-group-details'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { mapCriteriaToObject } from '@App/helpers/map-criteria-to-object'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import { HasSearchCriteria } from '@App/stores/search/helpers/has-search-criteria'
import { v4 as uuidv4 } from 'uuid'
import {
    getUnmatchedRightsholderSearchResults,
    getUnmatchedSongwriterSearchResults,
} from '@App/api/routes/rightsholder/matching'
import { PreselectedMatchWork } from '@Entities/matching/preselected-match-work'

export interface MatchSearchParameters {
    recordingTitle: string
    isrc: string
    writer: string
    artist: string
}

const dspRecordingOptions: Array<SelectOptionHide<MatchSearchParameters>> = [
    // For the current designs these are static fields without being included in criteria dropdown but may be included in the future -- including for completion
    { id: uuidv4(), value: 'recordingTitle', label: 'Recording Title', shouldHide: true, exactMatchOnly: true },
    { id: uuidv4(), value: 'isrc', label: 'Recording ISRC', shouldHide: true },
    { id: uuidv4(), value: 'writer', label: 'Writer', shouldHide: false, exactMatchOnly: true },
    {
        id: uuidv4(),
        value: 'artist',
        label: 'Recording Artist',
        shouldHide: false,
        exactMatchOnly: true,
    },
]

const defaultMatchingCriteria: Array<SearchCriterion<MatchSearchParameters>> = [
    {
        id: dspRecordingOptions[0].id,
        selectedOption: dspRecordingOptions[0],
        searchTerm: undefined,
        isFixed: true,
        isDisabled: true,
    },
    {
        id: dspRecordingOptions[1].id,
        selectedOption: dspRecordingOptions[1],
        searchTerm: undefined,
        isFixed: true,
        isDisabled: true,
    },
]

export interface MatchSearchFilter {
    unmatchedValue: string | null
    usageSource: string | null
    usagePeriod: string | null
}

const defaultMatchSearchFilter: MatchSearchFilter = {
    unmatchedValue: null,
    usageSource: null,
    usagePeriod: null,
}

const withRecordingTitle = (
    criteria: Array<SearchCriterion<MatchSearchParameters>>,
    recordingTitle: string
): Array<SearchCriterion<MatchSearchParameters>> =>
    criteria.map((criterion, index) =>
        index === 0 ? { ...criterion, id: uuidv4(), searchTerm: recordingTitle } : criterion
    )

export default class MatchingStore implements HasSearchCriteria<MatchSearchParameters> {
    createMatchSearchCriteriaStore(recordingTitle?: string): SearchCriteriaStore<MatchSearchParameters> {
        return new SearchCriteriaStore(
            recordingTitle ? withRecordingTitle(defaultMatchingCriteria, recordingTitle) : defaultMatchingCriteria,
            dspRecordingOptions,
            SearchOptionType.primary,
            RequiredCriteria.some,
            4
        )
    }

    private rootStore: RootStore

    private _unmatchedSearchResults: RecordingGroupList | undefined

    private _unmatchedSearchResultsPage: number

    private _selectedUnmatchedRecordingGroups: string[] = []

    private _currentSearchCriteriaStore: SearchCriteriaStore<MatchSearchParameters>

    private _matchingSearchCriteria: Partial<MatchSearchParameters>

    private _matchingFilterCriteria: Partial<MatchSearchFilter> = defaultMatchSearchFilter

    private _selectedWorkTitle: string | undefined

    private _preselectedWork: PreselectedMatchWork | undefined

    constructor(rootStore: RootStore) {
        makeObservable<
            MatchingStore,
            | '_unmatchedSearchResults'
            | '_selectedUnmatchedRecordingGroups'
            | '_selectedWorkTitle'
            | '_preselectedWork'
            | '_currentSearchCriteriaStore'
            | '_matchingSearchCriteria'
            | '_matchingFilterCriteria'
        >(this, {
            _unmatchedSearchResults: observable,
            _selectedUnmatchedRecordingGroups: observable,
            _selectedWorkTitle: observable,
            _preselectedWork: observable,
            _currentSearchCriteriaStore: observable,
            _matchingSearchCriteria: observable,
            _matchingFilterCriteria: observable,
            resetStore: action,
            clearSelectedUnmatchedRecordingGroups: action,
            selectUnmatchedRecordingGroup: action,
            deSelectUnmatchedRecordingGroup: action,
            setSelectedWorkTitle: action,
            setPreselectedWork: action,
            resetSearchCriteriaStore: action,
            setMatchingSearchCriteria: action,
            seedSearchFromUrl: action,
            setMatchingFilterCriteria: action,
            selectedUnmatchedRecordingGroupsLength: computed,
        })

        this.rootStore = rootStore
        this.resetSearchCriteriaStore()
    }

    get matchingSearchCriteria(): Partial<MatchSearchParameters> {
        return this._matchingSearchCriteria
    }

    get matchingFilterCriteria(): Partial<MatchSearchFilter> {
        return this._matchingFilterCriteria
    }

    get unmatchedSearchResults() {
        return this._unmatchedSearchResults
    }

    get selectedUnmatchedRecordingGroups() {
        return this._selectedUnmatchedRecordingGroups
    }

    get selectedUnmatchedRecordingGroupsLength() {
        return this._selectedUnmatchedRecordingGroups.length
    }

    get searchCriteriaStore(): SearchCriteriaStore<MatchSearchParameters> {
        return this._currentSearchCriteriaStore
    }

    get selectedWorkTitle() {
        return this._selectedWorkTitle
    }

    get preselectedWork() {
        return this._preselectedWork
    }

    selectUnmatchedRecordingGroup = (id: string) => this._selectedUnmatchedRecordingGroups.push(id)

    deSelectUnmatchedRecordingGroup = (id: string) => {
        this._selectedUnmatchedRecordingGroups.splice(this._selectedUnmatchedRecordingGroups.indexOf(id), 1)
    }

    async loadNextUnmatchedSearchResultsPage() {
        return this.loadUnmatchedSearchResults(this._unmatchedSearchResultsPage + 1)
    }

    async loadUnmatchedSearchResults(page: number): Promise<void> {
        this._unmatchedSearchResultsPage = page

        const criteriaToSend = {
            ...this._matchingFilterCriteria,
        }

        // Ignore Zero Value
        if (criteriaToSend.unmatchedValue === '0') {
            delete criteriaToSend.unmatchedValue
        }
        // if (this.openFeatureClient.getBooleanValue('use-new-matching-search', false)) {
        const adjustedPage = page - 1
        const response = this.rootStore.rightsholderStore.optionalCurrentRightsholderId
            ? await getUnmatchedRightsholderSearchResults(
                  this.rootStore.rightsholderStore.currentRightsholderId,
                  this.matchingSearchCriteria,
                  criteriaToSend,
                  adjustedPage
              )
            : await getUnmatchedSongwriterSearchResults(
                  this.rootStore.songwriterStore.currentSongwriterId,
                  this.matchingSearchCriteria,
                  criteriaToSend,
                  adjustedPage
              )
        const recordingGroupList = {
            recordingGroups: response.data.content,
            count: response.data.totalElements,
        }
        runInAction(() => {
            page === 1
                ? (this._unmatchedSearchResults = recordingGroupList)
                : (this._unmatchedSearchResults = {
                      count: this._unmatchedSearchResults?.count ?? 0,
                      recordingGroups:
                          this._unmatchedSearchResults?.recordingGroups
                              .slice()
                              .concat(recordingGroupList.recordingGroups) ?? [],
                  })
        })
    }

    setMatchingSearchCriteria(matchingSearchCriteria: Partial<MatchSearchParameters>) {
        this._matchingSearchCriteria = matchingSearchCriteria
    }

    /**
     * Starts a visit from a link's search terms and filters. Run it before the page first renders: it swaps in a
     * criteria store seeded from the URL and, if the terms satisfy the form's own "ready to search" rule, makes
     * them the search. Returns whether there is a search for the caller to run.
     */
    seedSearchFromUrl(criteria: Record<string, unknown>, filter?: Partial<MatchSearchFilter>): boolean {
        this._currentSearchCriteriaStore = this.createMatchSearchCriteriaStore()
        this._currentSearchCriteriaStore.applyCriteriaFromObject(criteria)
        const rows = this._currentSearchCriteriaStore.searchCriteria
        if (!this._currentSearchCriteriaStore.isCriteriaSearchReady(rows)) {
            return false
        }
        this._matchingSearchCriteria = mapCriteriaToObject(rows)
        this._matchingFilterCriteria = { ...defaultMatchSearchFilter, ...filter }
        return true
    }

    setMatchingFilterCriteria(matchingFilterCriteria: Partial<MatchSearchFilter>) {
        this._matchingFilterCriteria = matchingFilterCriteria
    }

    setSelectedWorkTitle(selectedWorkTitle: string) {
        this._selectedWorkTitle = selectedWorkTitle
    }

    setPreselectedWork(preselectedWork: PreselectedMatchWork) {
        this._preselectedWork = preselectedWork
        this._selectedWorkTitle = preselectedWork.title
        this._currentSearchCriteriaStore = this.createMatchSearchCriteriaStore(preselectedWork.title)
    }

    clearSelectedUnmatchedRecordingGroups(): void {
        this._selectedUnmatchedRecordingGroups = []
    }

    resetStore(): void {
        this.resetSearchCriteriaStore()
        runInAction(() => {
            this._unmatchedSearchResults = undefined
            this._selectedUnmatchedRecordingGroups = []
            this._selectedWorkTitle = undefined
            this._preselectedWork = undefined
            this._matchingSearchCriteria = {}
            this._matchingFilterCriteria = defaultMatchSearchFilter
        })
    }

    resetSearchCriteriaStore(): void {
        this._currentSearchCriteriaStore = this.createMatchSearchCriteriaStore()
    }
}
